import React, { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import LinkSidebarTransition from "@Shared/Components/LinkSidebar";

import {
  styleguideService,
  styleguidePropertyKeys,
} from "@Shared/Service/StyleguideService";

import {
  DateTime,
  FormatPicker,
  ButtonGroup,
  IconsSidebar,
  TextAreaInput as TextArea,
  TextInput as Text,
} from "@caelor/ui";
import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import LinkPicker from "../../../../../../components/ui/link-picker/LinkPicker";

import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { BOOLEAN_OPTIONS, CORNERS_OPTIONS } from "./options";
import { COUNTDOWN_PROPS, DESIGN_OPTIONS, DESIGN_STYLES } from "./defaults";
import { useCountdownStore } from "../../../../../../stores/use-countdown-store";
import ImagePicker from "../../../../../../components/ui/image-picker/ImagePicker";

const CountdownSettings = () => {
  const [searchValue, setSearchValue] = useState("");
  const [appIcons, setAppIcons] = useState(false);

  const { t } = useTranslation();
  const { data, setData, action, setAction } = useCountdownStore((state) => ({
    data: state.data,
    setData: state.setData,
    action: state.action,
    setAction: state.setAction,
  }));

  useEffect(() => {
    styleguideService
      .getProperty(styleguidePropertyKeys.APP_ICONS)
      .then((response) => response && setAppIcons(response.data || []))
      .catch((e) => console.log(e));
  }, []);

  const handleTimerLayoutChange = (isOn, prop) => {
    let timerLayout = [];
    if (isOn) {
      timerLayout = [...data[COUNTDOWN_PROPS.TIMER_LAYOUT]];
      timerLayout.push({ value: prop });
    } else {
      timerLayout = data[COUNTDOWN_PROPS.TIMER_LAYOUT].filter(
        (layout) => layout.value !== prop
      );
    }
    setData({
      [COUNTDOWN_PROPS.TIMER_LAYOUT]: timerLayout,
    });
  };

  return (
    <>
      {action && action.drawer === "link" && (
        <LinkSidebarTransition
          isOpen={action.drawer === "link"}
          setIsOpen={() => setAction(undefined)}
          value={data[COUNTDOWN_PROPS.URL]}
          onChange={(value) => {
            setData({ [COUNTDOWN_PROPS.URL]: value });
            setAction(undefined);
          }}
        />
      )}

      {action && action.drawer === "icon" && (
        <IconsSidebar
          appIcons={appIcons}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          isOpen={action.drawer === "icon"}
          setIsOpen={() => setAction(undefined)}
          containerStyles={{ top: "75px" }}
          onEmojiChange={(value) => setData({ [COUNTDOWN_PROPS.ICON]: value })}
          onIconChange={(value) => setData({ [COUNTDOWN_PROPS.ICON]: value })}
          onAppChange={(value) => setData({ [COUNTDOWN_PROPS.ICON]: value })}
          selectedIcon={data[COUNTDOWN_PROPS.ICON]}
          tabs={["Icons", "Emojis", "Logos"]}
          activeTab={data[COUNTDOWN_PROPS.ICON]?.native ? "Emojis" : "Icons"}
        />
      )}

      {DESIGN_OPTIONS.IMAGE.includes(data[COUNTDOWN_PROPS.TIMER_TYPE]) && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("image")}</SettingsOptionHeading>
          <ImagePicker
            hasImageReposition
            image={data[COUNTDOWN_PROPS.IMAGE]}
            onChange={(value) => {
              setData({ [COUNTDOWN_PROPS.IMAGE]: value });
            }}
          />
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>
        {DESIGN_OPTIONS.ICON.includes(data[COUNTDOWN_PROPS.TIMER_TYPE]) && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={
                  !!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
                    (layout) => layout.value === "icon"
                  )
                }
                onClick={(isOn) => handleTimerLayoutChange(isOn, "icon")}
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}

        <OptionContainer>
          <OptionLabel>{t("subtext")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                !!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
                  (layout) => layout.value === "text"
                )
              }
              onClick={(isOn) => handleTimerLayoutChange(isOn, "text")}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>

        <OptionContainer>
          <OptionLabel>
            {data[COUNTDOWN_PROPS.TIMER_TYPE] !== DESIGN_STYLES.SMALL_COUNTDOWN
              ? t("label")
              : t("link-text")}
          </OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                !!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
                  (layout) => layout.value === "label"
                )
              }
              onClick={(isOn) => handleTimerLayoutChange(isOn, "label")}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>

        {DESIGN_OPTIONS.ARROW.includes(data[COUNTDOWN_PROPS.TIMER_TYPE]) && (
          <OptionContainer>
            <OptionLabel>{t("arrow")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={
                  !!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
                    (layout) => layout.value === "arrow"
                  )
                }
                onClick={(isOn) => handleTimerLayoutChange(isOn, "arrow")}
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel alignToTop>{t("link-to")}</OptionLabel>
          <OptionContent>
            <LinkPicker
              value={data[COUNTDOWN_PROPS.URL]}
              onChange={(value) => setData({ [COUNTDOWN_PROPS.URL]: value })}
            />
          </OptionContent>
        </OptionContainer>
        {data[COUNTDOWN_PROPS.URL]?.source !== "email" && (
          <OptionContainer>
            <OptionLabel>{t("new-tab")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={data[COUNTDOWN_PROPS.NEW_TAB]}
                onClick={(value) =>
                  setData({ [COUNTDOWN_PROPS.NEW_TAB]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
        {data[COUNTDOWN_PROPS.TIMER_TYPE] !== DESIGN_STYLES.SMALL_COUNTDOWN &&
          data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
            (layout) => layout.value === "icon"
          ) && (
            <OptionContainer>
              <OptionLabel>{t("icon")}</OptionLabel>
              <OptionContent>
                <IconPicker
                  defaultTab={
                    data[COUNTDOWN_PROPS.ICON]?.native ? "Emojis" : "Icons"
                  }
                  tabs={["Icons", "Emojis", "Logos"]}
                  icon={data[COUNTDOWN_PROPS.ICON]}
                  onChange={({ value }) =>
                    setData({ [COUNTDOWN_PROPS.ICON]: value })
                  }
                />
              </OptionContent>
            </OptionContainer>
          )}
        <OptionContainer>
          <OptionLabel>{t("title")}</OptionLabel>
          <OptionContent>
            <Text
              placeholder={t("add")}
              value={data[COUNTDOWN_PROPS.TITLE]}
              onChange={(value) => setData({ [COUNTDOWN_PROPS.TITLE]: value })}
            />
          </OptionContent>
        </OptionContainer>
        {!!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
          (layout) => layout.value === "text"
        ) && (
          <OptionContainer>
            <OptionLabel>{t("subtext")}</OptionLabel>
            <OptionContent>
              <TextArea
                placeholder={t("add")}
                value={data[COUNTDOWN_PROPS.TEXT]}
                onChange={({ target: { value } }) =>
                  setData({ [COUNTDOWN_PROPS.TEXT]: value })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
        {!!data[COUNTDOWN_PROPS.TIMER_LAYOUT].find(
          (layout) => layout.value === "label"
        ) && (
          <OptionContainer>
            <OptionLabel>
              {data[COUNTDOWN_PROPS.TIMER_TYPE] !==
              DESIGN_STYLES.SMALL_COUNTDOWN
                ? t("label")
                : t("link-text")}
            </OptionLabel>
            <OptionContent>
              <Text
                placeholder={t("add")}
                value={data[COUNTDOWN_PROPS.LINK_TEXT]}
                onChange={(value) =>
                  setData({ [COUNTDOWN_PROPS.LINK_TEXT]: value })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
        <OptionContainer
          style={{ flexDirection: "column", alignItems: "flex-start" }}
        >
          <OptionContainer style={{ width: "100%" }}>
            <OptionLabel>{t("date-time")}</OptionLabel>
            <FormatPicker
              value={
                data[COUNTDOWN_PROPS.FORMAT] === "24" ? "HH:mm" : "hh:mm A"
              }
              setValue={(value) =>
                setData({
                  [COUNTDOWN_PROPS.FORMAT]: value === "HH:mm" ? "24" : "12",
                })
              }
            />
          </OptionContainer>
          <OptionContent style={{ width: "100%" }}>
            <DateTime
              value={data[COUNTDOWN_PROPS.TIME]}
              timeFormat={
                data[COUNTDOWN_PROPS.FORMAT] === "24" ? "HH:mm" : "hh:mm A"
              }
              onChange={(time) => setData({ [COUNTDOWN_PROPS.TIME]: time })}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={CORNERS_OPTIONS}
              value={data[COUNTDOWN_PROPS.BORDER_RADIUS]}
              options={{ input: true }}
              onClick={(value) =>
                setData({ [COUNTDOWN_PROPS.BORDER_RADIUS]: value })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        {data[COUNTDOWN_PROPS.TIMER_TYPE] === DESIGN_STYLES.CUSTOM_COLOR && (
          <OptionContainer>
            <OptionLabel>{t("color-mute")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={data[COUNTDOWN_PROPS.COLOR_MUTE]}
                onClick={(value) =>
                  setData({ [COUNTDOWN_PROPS.COLOR_MUTE]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
        <OptionContainer>
          <OptionLabel alignToTop>{t("colors")}</OptionLabel>
          <OptionContent>
            <ColorPicker
              hideColorTiles
              value={{
                accent: data?.colorFields?.Timer,
                text: data?.colorFields?.Timer,
              }}
              onChange={({ accent }) =>
                setData({ colorFields: { Timer: accent } })
              }
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
    </>
  );
};

export default CountdownSettings;
